import React, { useContext } from "react";
import { Helmet } from "react-helmet";
import Layout from "../layouts/default";

import styled from "styled-components";

import richText from "../utils/richText";
import { Body, Header2, Header5 } from "../components/typography";
import DomeHeader from "../components/DomeHeader";
import CalloutList from "../sections/CalloutList";
import ReviewCarousel from "../sections/ReviewCarousel";

import { PrimaryButton } from "../components/Button";
import RatingStars from "../components/RatingStars";
import ProductImageSlider from "../components/ProductImageSlider";
import { Colors } from "../components/styles/Colors";

import { store } from "../utils/StoreContext";
import Breakpoints from "../components/styles/Breakpoints";
import { graphql, navigate } from "gatsby";

const Items = styled.div`
  display: flex;
  width: 100%;
  margin: 0 auto;
  flex-wrap: wrap;
  justify-content: center;

  padding: 120px 0;

  background: ${Colors.hunter};

  color: ${Colors.cream1};

  .item {
      width: 100%;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0px 20px;
      margin-bottom: 30px;

      @media ${Breakpoints.desktop} {
        width: 25%;
        margin: 0;
      }

      img {
          width: 100px;
          margin-bottom: 20px;
      }

      ${Header5} {
        color: ${Colors.cream1};
    }

      p {
  color: ${Colors.cream1};

      }
  }
`

const Wrapper = styled.div`
  background: ${Colors.cream2};

  @media ${Breakpoints.tablet} {
    display: flex;

    flex-direction: row-reverse;

    padding: 30px 0 60px;

    > div {
      flex: 0 0 50%;
    }

    > div:last-child {
      padding-left: 25px;
      margin-right: 30px;
    }
  }

  @media ${Breakpoints.desktop} {
    > div:last-child {
      padding-left: 60px;
    }
  }
`;

const DetailsWrapper = styled.div`
  padding: 40px 32px;

  > ${Header2}:first-of-type {
    margin: 24px 0 40px;
  }

  @media ${Breakpoints.tablet} {
    max-width: 480px;
    margin: 0 auto;
  }
`;

const Description = styled.div`
  p {
    margin: 16px 0;
  }
`;

const VolumeOrSize = styled(Body)`
  font-size: 14px;
  margin: 24px 0 32px;
`;

const ButtonsContainer = styled.div`
  display: flex;

  flex-direction: column-reverse;

  & > button {
    margin-right: 20px;
    margin-top: 20px;
  }

  & > div {
    flex: 0 0 auto;
    height: 64px;
  }

  & > ${Header5} {
    margin-bottom: 20px;
  }

  @media ${Breakpoints.tablet} {
    flex-direction: row;
    align-items: center;

    & > button {
      margin-top: 0;
    }

    & > ${Header5} {
      margin-left: 15px;
      margin-bottom: 0px;
    }
  }
`;

const Disclaimer = styled(Body)`
  font-size: 13px;
  margin: 8px 0;
`;
const Striked = styled(Body)`
  text-decoration: line-through;
`;

export const query = graphql`
  query StarterTrial {
    contentfulFreeTrial(name: {eq: "Free Trial"}) {
      description {
        raw
      }
      images {
        fluid(quality: 100) {
        ...GatsbyContentfulFluid_withWebp
        }
      }
    }
    allContentfulHomepage {
      nodes {
        reviewCarouselItems {
          quote
          quoteSource
          image {
            fluid(maxWidth: 1440, quality: 100) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
        }
      }
    }
  }
`;

export default function FreeTrial({ data, location }) {
  const { dispatch, state } = useContext(store);
  const { description, images } = data.contentfulFreeTrial;
  const { reviewCarouselItems } = data.allContentfulHomepage.nodes[0];

  if (!process.env.GATSBY_SUBSCRIPTIONS) {
    return null;
  }

  function addToCart() {
      navigate('/free-trial-form');
  }

  return (
    <Layout pathName={location.pathname}>
      <Helmet>
        <title>Free Trial</title>
      </Helmet>
      <Wrapper>
        <ProductImageSlider slides={images} />
        <div>
          <DetailsWrapper>
            <DomeHeader text="3 Easy Steps" />
            <Header2>Try MANTL for free!</Header2>
            <Description style={{ color: Colors.hunter }}>
                <Body>
                  {richText(description.raw)}
                  <Striked as="span">$30</Striked> <strong >FREE + $4.99 Shipping</strong>
                </Body>
            </Description>

            <RatingStars score={5} count={153} />

            <ButtonsContainer>
              <PrimaryButton onClick={addToCart}>
                Get Started - $0 + $4.99 shipping
              </PrimaryButton>
            </ButtonsContainer>
            <Disclaimer>
            * No commitment, cancel anytime! For new members only. You’ll be enrolled into a subscription for a 30-day supply every month after the free trial at $25/month (a $5 savings on retail).
            </Disclaimer>
          </DetailsWrapper>
        </div>
      </Wrapper>
      <Items>
        <div className="item">
              <img src="https://images.ctfassets.net/zkye4xe6qnvp/3rjE2NamXMV8kVRAkvbaQD/2c2fbf76bc5a5cbf2f022dcc51da553d/SaveMoney.svg" alt="" />
              <Header5>Cancel Anytime</Header5>
              <Body>Try your first month free on us. If you don't love it, cancel your subscription at anytime in your account settings during your trial.</Body>
        </div>
        <div className="item">
              <img src="https://images.ctfassets.net/zkye4xe6qnvp/4sCuPXFJ1qAjositbjueXF/22082402c0441d686e9ce52ed7e5f5de/Guaranteed.svg" alt="" />
              <Header5>Save Money</Header5>
              <Body>If you choose not to cancel, in 30 days, we’ll ship you a 30-day supply of our Essentials subscription box for $25 (a $5 discount on retail price!). Your box will be shipped and billed every 30 days.</Body>
        </div>
        <div className="item">
              <img src="https://images.ctfassets.net/zkye4xe6qnvp/2Wyn45IRYQG4r6i9ZWVeTR/f62aae03a98c7876af5c5e3ecd29d15f/Control.svg" alt="" />
              <Header5>You're in Control</Header5>
              <Body>You can cancel, delay an order, add/remove products to your box at any time.</Body>
        </div>
      </Items>
      <ReviewCarousel items={reviewCarouselItems} />
    </Layout>
  );
}
